import { useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.js?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

function base64ToBlob(base64Data: string, contentType: string) {
  const byteCharacters = atob(base64Data.split(',')[1]);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

export function useGetPdfImages() {
  const getImages = useCallback(async (file: File, pageNumber?: number) => {
    const arrayBuffer = await file.arrayBuffer();
    const typedarray = new Uint8Array(arrayBuffer);
    const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
    const images = [];
    const numPages = pdf.numPages;

    const pagesToProcess = pageNumber
      ? [pageNumber]
      : Array.from({ length: numPages }, (_, i) => i + 1);

    for (const pageNum of pagesToProcess) {
      if (pageNum < 1 || pageNum > numPages) continue;
      const page = await pdf.getPage(pageNum);
      const scale = 2;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas context is null');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const base64 = canvas.toDataURL('image/png');
      const pngBlob = base64ToBlob(base64, 'image/png');
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      const imageName = `${originalName}_page${pageNum}.png`;
      const pngFile = new File([pngBlob], imageName, { type: 'image/png' });
      images.push({
        base64,
        size: pngBlob.size,
        name: imageName,
        file: pngFile,
      });
    }
    return images;
  }, []);

  return { getImages };
}
